"use client";

import { Children, useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const AUTOPLAY_MS = 5000;

const arrow_class =
    "flex size-10 cursor-pointer items-center justify-center rounded-full border border-jmt-neutral-200 bg-jmt-white text-jmt-neutral-900 shadow-sm transition hover:bg-jmt-neutral-100 disabled:cursor-default disabled:opacity-40";

/** جهت اسکرول در RTL منفی است؛ «بعدی» همیشه به سمت انتهای محتوا می‌رود. */
const direction_of = (el) => (getComputedStyle(el).direction === "rtl" ? -1 : 1);

/**
 * اسلایدر هیرو ادز: اسلایدها scroll-snap هستند، فلش‌ها و نقطه‌ها واقعی‌اند (تعداد نقطه = تعداد
 * اسلاید) و هر ۵ ثانیه خودکار جلو می‌رود؛ با هاور/لمس متوقف می‌شود و در آخر به اول برمی‌گردد.
 */
export function HeroSlider({ children, label = "بنر" }) {
    const slides = Children.toArray(children);
    const count = slides.length;
    const scroller = useRef(null);
    const refs = useRef([]);
    const [active, setActive] = useState(0);
    const paused = useRef(false);

    const go_to = useCallback((index) => {
        const root = scroller.current;
        if (!root) return;
        const i = (index + count) % count;
        setActive(i);
        const left = i * root.clientWidth * direction_of(root);
        root.scrollTo({ left, behavior: "smooth" });
        // اسکرول نرم در بعضی محیط‌ها (تب پس‌زمینه، کاهش حرکت) اجرا نمی‌شود؛ اگر نرسید، مستقیم می‌رویم
        setTimeout(() => {
            if (Math.abs(root.scrollLeft - left) > 2) root.scrollTo({ left, behavior: "instant" });
        }, 900);
    }, [count]);

    // اسلاید فعال از روی موقعیت اسکرول؛ هم با کشیدن و هم با دکمه درست می‌ماند
    useEffect(() => {
        const root = scroller.current;
        if (!root) return;
        const on_scroll = () => {
            const width = root.clientWidth || 1;
            setActive(Math.min(count - 1, Math.max(0, Math.round(Math.abs(root.scrollLeft) / width))));
        };
        root.addEventListener("scroll", on_scroll, { passive: true });
        return () => root.removeEventListener("scroll", on_scroll);
    }, [count]);

    useEffect(() => {
        if (count < 2) return;
        const timer = setInterval(() => {
            if (!paused.current && !document.hidden) go_to(active + 1);
        }, AUTOPLAY_MS);
        return () => clearInterval(timer);
    }, [active, count, go_to]);

    return (
        <section
            aria-label={label}
            aria-roledescription="carousel"
            className="relative w-full overflow-hidden"
            onMouseEnter={() => (paused.current = true)}
            onMouseLeave={() => (paused.current = false)}
            onTouchStart={() => (paused.current = true)}
            onTouchEnd={() => (paused.current = false)}>
            <div ref={scroller} className="hide-scrollbar flex snap-x snap-mandatory overflow-x-auto scroll-smooth">
                {slides.map((slide, index) => (
                    <div
                        key={index}
                        ref={(node) => (refs.current[index] = node)}
                        aria-hidden={index !== active}
                        className="h-[200px] w-full shrink-0 snap-start lg:h-[400px]">
                        {slide}
                    </div>
                ))}
            </div>

            {count > 1 && (
                <>
                    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 lg:bottom-6">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                aria-label={`اسلاید ${index + 1}`}
                                aria-current={index === active}
                                onClick={() => go_to(index)}
                                className={`h-2 cursor-pointer rounded-full transition-all ${index === active ? "w-5 bg-jmt-white" : "w-2 bg-jmt-white/60"}`}
                            />
                        ))}
                    </div>

                    <div className="absolute bottom-6 right-6 hidden items-center gap-3 lg:flex">
                        <button type="button" aria-label="اسلاید قبلی" onClick={() => go_to(active - 1)} className={arrow_class}>
                            <ChevronRight size={20} aria-hidden="true" />
                        </button>
                        <button type="button" aria-label="اسلاید بعدی" onClick={() => go_to(active + 1)} className={arrow_class}>
                            <ChevronLeft size={20} aria-hidden="true" />
                        </button>
                    </div>
                </>
            )}
        </section>
    );
}
